import { formatDate } from '../utils/dates.ts'
import React, { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { AgGridReact } from 'ag-grid-react'
import { ColDef, GridReadyEvent } from 'ag-grid-community'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-quartz.css'
import { api } from '../services/api.ts'
import LoadingSpinner from '../components/LoadingSpinner.tsx'
import toast from 'react-hot-toast'
import { useTheme } from '../contexts/ThemeContext.tsx'
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  XMarkIcon,
  PlusIcon,
} from '@heroicons/react/24/outline'
import { formatPhoneNumber } from '../utils/phoneFormatter.ts'

// HIGH VALUE = vape/tobacco distributor or wholesaler. Newer stores carry the
// backend's is_high_value flag (known distributors, AI, name + vape context);
// older stores fall back to the original name + keyword check.
const isHighValueStore = (store: any): boolean => {
  if (!store) return false
  if (store.is_high_value) return true
  const keywordLower = (store.keyword_used || '').toLowerCase()
  const nameLower = (store.name || '').toLowerCase()
  const isDistributorKeyword =
    keywordLower.includes('wholesale') || keywordLower.includes('distributor')
  const isDistributorName =
    nameLower.includes('wholesale') ||
    nameLower.includes('distributor') ||
    nameLower.includes('distribution')
  return isDistributorKeyword && isDistributorName
}

export default function Stores() {
  const { theme } = useTheme()
  const [jobFilter, setJobFilter] = useState<string>('')
  const [includeDuplicates, setIncludeDuplicates] = useState<boolean>(false)
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [stateFilter, setStateFilter] = useState<string>('')
  const [sourceFilter, setSourceFilter] = useState<string>('')
  const [keywordFilter, setKeywordFilter] = useState<string>('')
  const [distributorFilter, setDistributorFilter] = useState<string>('')
  const [showFilters, setShowFilters] = useState<boolean>(false)
  const [showAddStoreModal, setShowAddStoreModal] = useState<boolean>(false)
  const [selectedJobForAdd, setSelectedJobForAdd] = useState<string>('')
  const [isAIFiltering, setIsAIFiltering] = useState<boolean>(false)
  const [nonVapeStores, setNonVapeStores] = useState<any[]>([])
  const [selectedStoreIds, setSelectedStoreIds] = useState<Set<string>>(
    new Set()
  )
  const [selectedCount, setSelectedCount] = useState<number>(0)
  const [gridApi, setGridApi] = useState<any>(null)
  const [showZohoCheckDialog, setShowZohoCheckDialog] = useState<boolean>(false)
  const [showZohoResultDialog, setShowZohoResultDialog] =
    useState<boolean>(false)
  const [storeToCheck, setStoreToCheck] = useState<any>(null)
  const [zohoCheckResult, setZohoCheckResult] = useState<any>(null)
  const [zohoDedupResult, setZohoDedupResult] = useState<any>(null)
  const [showZohoDedupDialog, setShowZohoDedupDialog] = useState<boolean>(false)
  const [phoneEnrichResult, setPhoneEnrichResult] = useState<any>(null)
  const [showPhoneEnrichDialog, setShowPhoneEnrichDialog] = useState<boolean>(false)
  const [fetchingStoreId, setFetchingStoreId] = useState<string | null>(null)
  const [newStore, setNewStore] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    phone: '',
    website: '',
    place_id: '',
    keyword_used: '',
    rating: '',
    rating_count: '',
  })
  const queryClient = useQueryClient()

  // Fetch all jobs for the filter dropdown
  const { data: jobs } = useQuery('all-jobs', () =>
    api.get('/api/jobs/?limit=1000')
  )

  // Delete store mutation
  const deleteStoreMutation = useMutation(
    (storeId: string) => api.delete(`/api/stores/${storeId}`),
    {
      onSuccess: () => {
        toast.success('Store deleted successfully')
        queryClient.invalidateQueries([
          'stores',
          jobFilter,
          includeDuplicates,
          searchTerm,
          stateFilter,
          sourceFilter,
          keywordFilter,
        ])
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.detail || 'Failed to delete store')
      },
    }
  )

  // Bulk delete mutation
  const bulkDeleteMutation = useMutation(
    (storeIds: string[]) =>
      api.post('/api/stores/bulk-delete', { store_ids: storeIds }),
    {
      onSuccess: () => {
        toast.success(`Deleted ${selectedCount} stores successfully`)
        setSelectedStoreIds(new Set())
        setSelectedCount(0)
        // Exit AI filter mode if it was active
        if (isAIFiltering) {
          setIsAIFiltering(false)
          setNonVapeStores([])
        }
        queryClient.invalidateQueries([
          'stores',
          jobFilter,
          includeDuplicates,
          searchTerm,
          stateFilter,
          sourceFilter,
          keywordFilter,
        ])
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.detail || 'Failed to delete stores')
      },
    }
  )

  // AI Filter mutation - filters only stores for the selected job
  const aiFilterMutation = useMutation(
    () => {
      const params = new URLSearchParams({ limit: '200' })
      if (jobFilter) {
        params.append('job_id', jobFilter)
      }
      return api.post(`/api/stores/ai-filter-non-vape?${params.toString()}`)
    },
    {
      onSuccess: (response) => {
        const nonVape = response.data.non_vape_stores || []
        const distributorsProtected = response.data.distributors_protected || 0
        const obviousVapeProtected = response.data.obvious_vape_protected || 0
        const storesChecked = response.data.stores_checked || 0
        setNonVapeStores(nonVape)
        setIsAIFiltering(true)

        let message = `Found ${nonVape.length} non-vape stores (AI checked ${storesChecked})`
        const protectedItems: string[] = []
        if (distributorsProtected > 0) {
          protectedItems.push(`${distributorsProtected} distributors`)
        }
        if (obviousVapeProtected > 0) {
          protectedItems.push(`${obviousVapeProtected} vape stores`)
        }
        if (protectedItems.length > 0) {
          message += ` · Protected: ${protectedItems.join(', ')}`
        }
        toast.success(message, { duration: 5000 })
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.detail || 'AI filtering failed')
      },
    }
  )

  // Check Zoho mutation
  // Fetch store details mutation
  const fetchDetailsMutation = useMutation(
    (storeId: string) => {
      setFetchingStoreId(storeId)
      return api.post(`/api/stores/${storeId}/fetch-details`)
    },
    {
      onSuccess: (response) => {
        const data = response.data
        toast.success(
          data.message ||
            `Successfully fetched details for ${data.store?.name || 'store'}`
        )
        setFetchingStoreId(null)
        queryClient.invalidateQueries([
          'stores',
          jobFilter,
          includeDuplicates,
          searchTerm,
          stateFilter,
          sourceFilter,
          keywordFilter,
        ])
      },
      onError: (error: any) => {
        const errorMessage =
          error.response?.data?.detail ||
          error.message ||
          'Failed to fetch store details'
        toast.error(errorMessage, { duration: 5000 })
        setFetchingStoreId(null)
      },
    }
  )

  const checkZohoMutation = useMutation(
    (storeId: string) => api.post(`/api/stores/${storeId}/check-zoho`),
    {
      onSuccess: (response) => {
        setZohoCheckResult(response.data)
        setShowZohoCheckDialog(false)
        setShowZohoResultDialog(true)
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.detail || 'Failed to check Zoho')
        setShowZohoCheckDialog(false)
      },
    }
  )

  // Export to Zoho mutation (bulk)
  const exportToZohoMutation = useMutation(
    (storeIds: string[]) =>
      api.post('/api/stores/export-to-zoho', { store_ids: storeIds }),
    {
      onSuccess: (response) => {
        const data = response.data
        toast.success(
          `Successfully exported ${data.leads_created || 0} stores to Zoho!`
        )
        if (data.duplicates_removed > 0) {
          toast(`${data.duplicates_removed} duplicates were skipped`, {
            icon: 'ℹ️',
          })
        }
        setSelectedStoreIds(new Set())
        setSelectedCount(0)
        setShowZohoResultDialog(false)
        queryClient.invalidateQueries(['stores'])
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.detail || 'Failed to export to Zoho')
      },
    }
  )

  // Zoho De Dup Check mutation
  const zohoDedupCheckMutation = useMutation(
    () => {
      const url = jobFilter
        ? `/api/stores/zoho-dedup-check?job_id=${jobFilter}`
        : '/api/stores/zoho-dedup-check'
      return api.post(url)
    },
    {
      onSuccess: (response) => {
        const data = response.data
        setZohoDedupResult(data)
        setShowZohoDedupDialog(true)
      },
      onError: (error: any) => {
        toast.error(
          error.response?.data?.detail || 'Failed to check Zoho duplicates'
        )
      },
    }
  )

  // Phone Enrichment mutation
  const phoneEnrichMutation = useMutation(
    () => {
      const url = jobFilter
        ? `/api/stores/enrich-phones?job_id=${jobFilter}`
        : '/api/stores/enrich-phones'
      return api.post(url)
    },
    {
      onSuccess: (response) => {
        const data = response.data
        setPhoneEnrichResult(data)
        setShowPhoneEnrichDialog(true)
        toast.success(
          data.message || `Found phones for ${data.phones_found} stores`
        )
        queryClient.invalidateQueries(['stores'])
      },
      onError: (error: any) => {
        toast.error(
          error.response?.data?.detail || 'Phone enrichment failed'
        )
      },
    }
  )

  // Zoho De Dup Delete mutation
  const zohoDedupDeleteMutation = useMutation(
    (storeIds: string[]) => api.post('/api/stores/zoho-dedup-delete', storeIds),
    {
      onSuccess: (response) => {
        const data = response.data
        toast.success(
          data.message ||
            `Successfully deleted ${data.deleted_count} duplicate stores`
        )
        setShowZohoDedupDialog(false)
        setZohoDedupResult(null)
        queryClient.invalidateQueries(['stores'])
      },
      onError: (error: any) => {
        toast.error(
          error.response?.data?.detail || 'Failed to delete duplicates'
        )
      },
    }
  )

  // Add store mutation
  const addStoreMutation = useMutation(
    (storeData: any) => api.post('/api/stores/', storeData),
    {
      onSuccess: () => {
        toast.success('Store added successfully')
        queryClient.invalidateQueries([
          'stores',
          jobFilter,
          includeDuplicates,
          searchTerm,
          stateFilter,
          sourceFilter,
          keywordFilter,
        ])
        queryClient.invalidateQueries('all-jobs')
        setShowAddStoreModal(false)
        setNewStore({
          name: '',
          address: '',
          city: '',
          state: '',
          zip_code: '',
          phone: '',
          website: '',
          place_id: '',
          keyword_used: '',
          rating: '',
          rating_count: '',
        })
        setSelectedJobForAdd('')
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.detail || 'Failed to add store')
      },
    }
  )

  // Fetch stores with optional filters
  const { data: stores, isLoading } = useQuery(
    [
      'stores',
      jobFilter,
      includeDuplicates,
      searchTerm,
      stateFilter,
      sourceFilter,
      keywordFilter,
    ],
    async () => {
      const params = new URLSearchParams({
        limit: '10000',
        include_duplicates: includeDuplicates.toString(),
      })

      if (jobFilter) {
        params.append('job_id', jobFilter)
      }
      if (stateFilter) {
        params.append('state', stateFilter)
      }
      if (keywordFilter) {
        params.append('keyword', keywordFilter)
      }

      const url = jobFilter
        ? `/api/stores/job/${jobFilter}?${params.toString()}`
        : `/api/stores/?${params.toString()}`

      return api.get(url)
    },
    {
      keepPreviousData: true,
    }
  )

  // Handler functions
  const handleDelete = (storeId: string, storeName: string) => {
    toast(
      (t) => (
        <div>
          <p className='font-medium text-gray-200 mb-3'>
            Are you sure you want to delete "{storeName}"?
          </p>
          <div className='flex space-x-2'>
            <button
              onClick={() => {
                deleteStoreMutation.mutate(storeId)
                toast.dismiss(t.id)
              }}
              className='px-3 py-1.5 bg-red-600 text-white text-sm font-medium rounded hover:bg-red-700'
            >
              Delete
            </button>
            <button
              onClick={() => toast.dismiss(t.id)}
              className='px-3 py-1.5 bg-gray-200 text-gray-800 text-sm font-medium rounded hover:bg-gray-300'
            >
              Cancel
            </button>
          </div>
        </div>
      ),
      {
        duration: 10000,
        position: 'top-center',
        style: {
          minWidth: '350px',
        },
      }
    )
  }

  const handleEdit = (storeData: any) => {
    toast('Double-click any cell to edit its value', { icon: 'ℹ️' })
  }

  // Column definitions for AG Grid
  const columnDefs: ColDef[] = useMemo(
    () => [
      // Checkbox selection column (always visible)
      {
        headerName: '',
        field: 'selection',
        checkboxSelection: true,
        headerCheckboxSelection: true,
        width: 50,
        pinned: 'left' as const,
        sortable: false,
        filter: false,
        resizable: false,
        editable: false,
      },
      {
        headerName: 'Actions',
        field: 'actions',
        sortable: false,
        filter: false,
        resizable: true,
        editable: false,
        width: 200,
        minWidth: 180,
        pinned: 'left' as const,
        cellRenderer: 'actionsRenderer',
      },
      {
        headerName: 'Name',
        field: 'name',
        filter: 'agTextColumnFilter',
        sortable: true,
        resizable: true,
        width: 350,
        cellStyle: { fontWeight: 500 },
        cellRenderer: (params: any) => {
          const name = params.value || ''
          // High-value distributor/wholesale lead
          const isDistributor = isHighValueStore(params.data)

          return (
            <div className='flex items-center space-x-2'>
              <span className='font-medium'>{name}</span>
              {isDistributor && (
                <span
                  title={params.data?.high_value_reason || 'Distributor / wholesaler'}
                  className='inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-gradient-to-r from-yellow-400 to-orange-500 text-white shadow-sm'
                >
                  💎 HIGH VALUE
                </span>
              )}
            </div>
          )
        },
      },
      {
        headerName: 'Phone',
        field: 'phone',
        filter: 'agTextColumnFilter',
        sortable: true,
        resizable: true,
        width: 150,
        valueGetter: (params) => {
          // Format the phone number for display
          return formatPhoneNumber(params.data?.phone)
        },
        // Keep original value for filtering/sorting
        getQuickFilterText: (params) => {
          // Use original phone value for filtering
          return params.data?.phone || ''
        },
      },
      {
        headerName: 'Address',
        field: 'address',
        filter: 'agTextColumnFilter',
        sortable: true,
        resizable: true,
        width: 300,
      },
      {
        headerName: 'City',
        field: 'city',
        filter: 'agTextColumnFilter',
        sortable: true,
        resizable: true,
        width: 150,
      },
      {
        headerName: 'State',
        field: 'state',
        filter: 'agSetColumnFilter',
        sortable: true,
        resizable: true,
        width: 80,
      },
      {
        headerName: 'Zip',
        field: 'zip_code',
        filter: 'agTextColumnFilter',
        sortable: true,
        resizable: true,
        width: 100,
      },

      {
        headerName: 'Website',
        field: 'website',
        filter: 'agTextColumnFilter',
        sortable: true,
        resizable: true,
        width: 200,
        cellRenderer: 'websiteRenderer',
      },
      {
        headerName: 'Rating',
        field: 'rating',
        filter: 'agNumberColumnFilter',
        sortable: true,
        resizable: true,
        width: 100,
        valueFormatter: (params: any) => {
          if (params.value) {
            return `⭐ ${params.value}`
          }
          return ''
        },
      },
      {
        headerName: 'Reviews',
        field: 'rating_count',
        filter: 'agNumberColumnFilter',
        sortable: true,
        resizable: true,
        width: 100,
      },
      {
        headerName: 'Keyword',
        field: 'keyword_used',
        filter: 'agSetColumnFilter',
        sortable: true,
        resizable: true,
        width: 150,
      },
      {
        headerName: 'Source',
        field: 'data_source',
        filter: 'agSetColumnFilter',
        sortable: true,
        resizable: true,
        width: 100,
        cellRenderer: (params: any) => {
          const source = params.value || 'google'
          const colors = {
            google:
              'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300',
            yelp: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300',
            osm: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300',
          }
          return (
            <span
              className={`px-2 py-1 text-xs rounded font-medium ${
                colors[source as keyof typeof colors] || colors.google
              }`}
            >
              {source.toUpperCase()}
            </span>
          )
        },
      },
      {
        headerName: 'AI Filtered',
        field: 'ai_filtered',
        filter: 'agSetColumnFilter',
        sortable: true,
        resizable: true,
        width: 120,
        cellRenderer: (params: any) => {
          if (params.value) {
            const confidence = params.data.ai_confidence
            const confidencePercent = confidence
              ? (confidence * 100).toFixed(0)
              : 'N/A'
            return (
              <div className='flex items-center space-x-1'>
                <span className='inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300'>
                  ✓
                </span>
                {confidence && (
                  <span className='text-xs text-gray-500 dark:text-gray-400'>
                    {confidencePercent}%
                  </span>
                )}
              </div>
            )
          }
          return (
            <span className='text-xs text-gray-400 dark:text-gray-500'>-</span>
          )
        },
      },
      {
        headerName: 'Status',
        field: 'status',
        filter: 'agSetColumnFilter',
        sortable: true,
        resizable: true,
        width: 120,
        cellRenderer: 'statusRenderer',
      },
      {
        headerName: 'Source',
        field: 'is_imported',
        filter: 'agSetColumnFilter',
        sortable: true,
        resizable: true,
        width: 100,
        cellRenderer: (params: any) => {
          if (params.value) {
            return (
              <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'>
                Imported
              </span>
            )
          }
          return (
            <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'>
              Scraped
            </span>
          )
        },
      },
    ],
    [handleDelete, handleEdit]
  )

  const defaultColDef: ColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      editable: true, // Make cells editable
    }),
    []
  )

  // Handler for checking store in Zoho
  const handleFetchDetails = (store: any) => {
    // Check if store is from Google Places (not Yelp or OSM)
    const dataSource = store.data_source || 'google'
    if (dataSource !== 'google') {
      toast.error(
        `This store is from ${dataSource.toUpperCase()}, not Google Places. Only stores from Google Places can have details fetched.`,
        { duration: 5000 }
      )
      return
    }

    if (!store.place_id) {
      toast.error(
        'This store does not have a place_id. Cannot fetch details.',
        { duration: 5000 }
      )
      return
    }

    // Check if it's a restored placeholder (can't fetch details for these)
    if (store.place_id.startsWith('restored_')) {
      toast.error(
        'This store was restored without a valid Google Places ID. Cannot fetch details.',
        { duration: 5000 }
      )
      return
    }

    // Additional check: OSM and Yelp place_ids have prefixes
    if (
      store.place_id.startsWith('osm_') ||
      store.place_id.startsWith('yelp_')
    ) {
      const source = store.place_id.split('_')[0].toUpperCase()
      toast.error(
        `This store has a ${source} place_id, not a Google Places ID. Only stores from Google Places can have details fetched.`,
        { duration: 5000 }
      )
      return
    }

    fetchDetailsMutation.mutate(store.id)
  }

  const handleCheckZoho = (store: any) => {
    setStoreToCheck(store)
    setShowZohoCheckDialog(true)
  }

  // Handler for confirming Zoho check
  const handleConfirmZohoCheck = () => {
    if (storeToCheck && storeToCheck.id) {
      checkZohoMutation.mutate(storeToCheck.id)
    }
  }

  // Custom cell renderer components
  const ActionsRenderer = (props: any) => {
    const storeId = props.data?.id
    const storeName = props.data?.name || ''
    if (!storeId) return null

    return (
      <div className='flex items-center space-x-1'>
        <button
          onClick={() => handleEdit(props.data)}
          className='px-2 py-1 text-xs font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded border border-blue-300'
          title='Edit store (double-click cells to edit)'
        >
          ✏️
        </button>
        <button
          onClick={() => handleFetchDetails(props.data)}
          className='px-2 py-1 text-xs font-medium text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded border border-purple-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:text-gray-400'
          title={
            (props.data?.data_source && props.data.data_source !== 'google') ||
            props.data?.place_id?.startsWith('osm_') ||
            props.data?.place_id?.startsWith('yelp_')
              ? `This store is from ${
                  props.data?.data_source?.toUpperCase() || 'another source'
                }, not Google Places. Only Google Places stores can fetch details.`
              : 'Fetch Place Details (phone, address, website, rating, etc.)'
          }
          disabled={
            (props.data?.data_source && props.data.data_source !== 'google') ||
            props.data?.place_id?.startsWith('osm_') ||
            props.data?.place_id?.startsWith('yelp_') ||
            (fetchDetailsMutation.isLoading && fetchingStoreId === storeId)
          }
        >
          {fetchDetailsMutation.isLoading && fetchingStoreId === storeId
            ? '⏳'
            : '📥'}
        </button>
        <button
          onClick={() => handleCheckZoho(props.data)}
          className='px-2 py-1 text-xs font-medium text-green-600 hover:text-green-800 hover:bg-green-50 rounded border border-green-300'
          title='Check if in Zoho'
          disabled={checkZohoMutation.isLoading}
        >
          {checkZohoMutation.isLoading && storeToCheck?.id === storeId
            ? '⏳'
            : '✓'}
        </button>
        <button
          onClick={() => handleDelete(storeId, storeName)}
          className='px-2 py-1 text-xs font-medium text-red-600 hover:text-red-800 hover:bg-red-50 rounded border border-red-300'
          title='Delete store'
        >
          🗑️
        </button>
      </div>
    )
  }

  const WebsiteRenderer = (props: any) => {
    if (props.value) {
      return (
        <a
          href={props.value}
          target='_blank'
          rel='noopener noreferrer'
          className='text-blue-600 hover:underline'
        >
          {props.value}
        </a>
      )
    }
    return null
  }

  const StatusRenderer = (props: any) => {
    if (props.value === 'OPERATIONAL') {
      return (
        <span className='px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800'>
          Operational
        </span>
      )
    } else if (props.value === 'CLOSED_TEMPORARILY') {
      return (
        <span className='px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800'>
          Temp. Closed
        </span>
      )
    } else if (props.value === 'CLOSED_PERMANENTLY') {
      return (
        <span className='px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800'>
          Closed
        </span>
      )
    }
    return <span>{props.value || ''}</span>
  }

  const frameworkComponents = {
    actionsRenderer: ActionsRenderer,
    websiteRenderer: WebsiteRenderer,
    statusRenderer: StatusRenderer,
  }

  const storesData = stores?.data || []

  // Get unique values for filter dropdowns
  const uniqueStates = useMemo(() => {
    const states = [...new Set(storesData.map((store: any) => store.state))]
      .filter(Boolean)
      .sort()
    return states as string[]
  }, [storesData])

  const uniqueKeywords = useMemo(() => {
    const keywords = [
      ...new Set(storesData.map((store: any) => store.keyword_used)),
    ]
      .filter(Boolean)
      .sort()
    return keywords as string[]
  }, [storesData])

  // Filter stores based on search term and source filter
  const filteredStores = useMemo(() => {
    // If in AI filtering mode, show only non-vape stores
    if (isAIFiltering) {
      return nonVapeStores
    }

    let filtered = storesData

    // Apply search term filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase()
      filtered = filtered.filter(
        (store: any) =>
          store.name.toLowerCase().includes(searchLower) ||
          store.address.toLowerCase().includes(searchLower) ||
          store.city.toLowerCase().includes(searchLower) ||
          store.phone?.toLowerCase().includes(searchLower) ||
          store.website?.toLowerCase().includes(searchLower)
      )
    }

    // Apply source filter
    if (sourceFilter) {
      if (sourceFilter === 'imported') {
        filtered = filtered.filter((store: any) => store.is_imported)
      } else if (sourceFilter === 'scraped') {
        filtered = filtered.filter((store: any) => !store.is_imported)
      }
    }

    // Apply distributor filter
    if (distributorFilter) {
      if (distributorFilter === 'distributors') {
        filtered = filtered.filter((store: any) => isHighValueStore(store))
      } else if (distributorFilter === 'non-distributors') {
        filtered = filtered.filter((store: any) => !isHighValueStore(store))
      }
    }

    return filtered
  }, [
    storesData,
    searchTerm,
    sourceFilter,
    distributorFilter,
    isAIFiltering,
    nonVapeStores,
  ])

  // Clear all filters
  const clearAllFilters = () => {
    setSearchTerm('')
    setStateFilter('')
    setSourceFilter('')
    setKeywordFilter('')
    setDistributorFilter('')
    setJobFilter('')
  }

  // Handle add store modal
  const handleOpenAddStoreModal = () => {
    if (!jobFilter) {
      toast.error('Please select a job first to add a store')
      return
    }
    setSelectedJobForAdd(jobFilter)
    setShowAddStoreModal(true)
  }

  const handleCloseAddStoreModal = () => {
    setShowAddStoreModal(false)
    setSelectedJobForAdd('')
    setNewStore({
      name: '',
      address: '',
      city: '',
      state: '',
      zip_code: '',
      phone: '',
      website: '',
      place_id: '',
      keyword_used: '',
      rating: '',
      rating_count: '',
    })
  }

  const handleAddStore = () => {
    if (!newStore.name.trim()) {
      toast.error('Store name is required')
      return
    }
    if (!newStore.phone.trim()) {
      toast.error('Phone number is required')
      return
    }

    const storeData = {
      job_id: selectedJobForAdd,
      name: newStore.name.trim(),
      address: newStore.address.trim(),
      city: newStore.city.trim(),
      state: newStore.state.trim().toUpperCase(),
      zip_code: newStore.zip_code.trim(),
      phone: newStore.phone.trim(),
      website: newStore.website.trim(),
      place_id: newStore.place_id.trim() || `manual_${Date.now()}`,
      keyword_used: newStore.keyword_used.trim() || 'manual',
      rating: newStore.rating ? parseFloat(newStore.rating) : null,
      rating_count: newStore.rating_count
        ? parseInt(newStore.rating_count)
        : null,
      is_imported: false,
    }

    addStoreMutation.mutate(storeData)
  }

  // Debug logging
  React.useEffect(() => {
    console.log('Stores data:', stores)
    console.log('Stores array:', storesData)
    console.log('Filtered stores:', filteredStores)
    console.log('Stores length:', storesData.length)
  }, [stores, storesData, filteredStores])

  // Handle grid ready
  const onGridReady = (params: GridReadyEvent) => {
    setGridApi(params.api)
  }

  // Handle selection change
  const onSelectionChanged = () => {
    if (gridApi) {
      const selectedRows = gridApi.getSelectedRows()
      const selectedIds = new Set<string>(selectedRows.map((row: any) => String(row.id)))
      setSelectedStoreIds(selectedIds)
      setSelectedCount(selectedIds.size)
    }
  }

  // Handle AI Filter
  const handleAIFilter = () => {
    aiFilterMutation.mutate()
  }

  // Handle Exit AI Filter
  const handleExitAIFilter = () => {
    setIsAIFiltering(false)
    setNonVapeStores([])
    setSelectedStoreIds(new Set())
    setSelectedCount(0)
  }

  // Handle Bulk Delete
  const handleBulkDelete = () => {
    if (selectedCount === 0) {
      toast.error('Please select stores to delete')
      return
    }

    toast(
      (t) => (
        <div>
          <p className='font-medium text-gray-200 mb-3'>
            Are you sure you want to delete {selectedCount} selected stores?
          </p>
          <div className='flex space-x-2'>
            <button
              onClick={() => {
                bulkDeleteMutation.mutate(Array.from(selectedStoreIds))
                toast.dismiss(t.id)
              }}
              className='px-3 py-1.5 bg-red-600 text-white text-sm font-medium rounded hover:bg-red-700'
            >
              Delete All
            </button>
            <button
              onClick={() => toast.dismiss(t.id)}
              className='px-3 py-1.5 bg-gray-200 text-gray-800 text-sm font-medium rounded hover:bg-gray-300'
            >
              Cancel
            </button>
          </div>
        </div>
      ),
      {
        duration: 10000,
        position: 'top-center',
        style: {
          minWidth: '350px',
        },
      }
    )
  }

  const handleBulkExportToZoho = () => {
    if (selectedCount === 0) {
      toast.error('Please select stores to export')
      return
    }

    toast(
      (t) => (
        <div>
          <p className='font-medium text-gray-200 mb-3'>
            Export {selectedCount} selected stores to Zoho CRM as leads?
          </p>
          <div className='flex space-x-2'>
            <button
              onClick={() => {
                exportToZohoMutation.mutate(Array.from(selectedStoreIds))
                toast.dismiss(t.id)
              }}
              className='px-3 py-1.5 bg-green-600 text-white text-sm font-medium rounded hover:bg-green-700'
            >
              Export to Zoho
            </button>
            <button
              onClick={() => toast.dismiss(t.id)}
              className='px-3 py-1.5 bg-gray-200 text-gray-800 text-sm font-medium rounded hover:bg-gray-300'
            >
              Cancel
            </button>
          </div>
        </div>
      ),
      {
        duration: 10000,
        position: 'top-center',
        style: {
          minWidth: '350px',
        },
      }
    )
  }

  // Handle cell value changes for inline editing
  const onCellValueChanged = (params: any) => {
    // Update the store via API
    const storeId = params.data.id
    const field = params.colDef.field
    const newValue = params.newValue

    toast
      .promise(api.put(`/api/stores/${storeId}`, { [field]: newValue }), {
        loading: 'Updating store...',
        success: 'Store updated successfully!',
        error: 'Failed to update store',
      })
      .then(() => {
        queryClient.invalidateQueries([
          'stores',
          jobFilter,
          includeDuplicates,
          searchTerm,
          stateFilter,
          sourceFilter,
          keywordFilter,
        ])
      })
  }

  if (isLoading) {
    return <LoadingSpinner />
  }

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-2xl font-bold text-gray-900 dark:text-white'>
            Stores
          </h1>
          <p className='mt-1 text-sm text-gray-500 dark:text-gray-400'>
            Browse and filter scraped store data ({filteredStores.length} of{' '}
            {storesData.length} stores)
          </p>
        </div>

        {/* Filters and Actions */}
        <div className='flex items-center space-x-6'>
          {/* Job Filter */}
          <div className='flex items-center space-x-2'>
            <label
              htmlFor='job-filter'
              className='text-sm font-medium text-gray-700 dark:text-gray-300'
            >
              Filter by Job:
            </label>
            <select
              id='job-filter'
              value={jobFilter}
              onChange={(e) => setJobFilter(e.target.value)}
              className='select w-64'
            >
              <option value=''>All Jobs</option>
              {(jobs?.data || []).map((job: any) => (
                <option key={job.id} value={job.id}>
                  {job.is_import_job ? job.import_filename : job.state} -{' '}
                  {job.total_stores} stores -{' '}
                  {formatDate(job.created_at)}
                </option>
              ))}
            </select>
          </div>

          {/* Add Store Button */}
          <button
            onClick={handleOpenAddStoreModal}
            disabled={!jobFilter}
            className='inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed'
          >
            <PlusIcon className='h-4 w-4 mr-2' />
            Add Store
          </button>

          {/* Include Duplicates Toggle */}
          {/* <label className='flex items-center space-x-2 cursor-pointer'>
            <input
              type='checkbox'
              checked={includeDuplicates}
              onChange={(e) => setIncludeDuplicates(e.target.checked)}
              className='rounded border-gray-300 text-blue-600 focus:ring-blue-500'
            />
            <span className='text-sm font-medium text-gray-700 dark:text-gray-300'>
              Include Duplicates
            </span>
          </label> */}
        </div>
      </div>

      {/* Search and Advanced Filters */}
      <div className='bg-white shadow rounded-lg p-4 dark:bg-gray-800'>
        <div className='space-y-4'>
          {/* Search Bar */}
          <div className='flex items-center space-x-4'>
            <div className='flex-1 relative'>
              <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                <MagnifyingGlassIcon className='h-5 w-5 text-gray-400' />
              </div>
              <input
                type='text'
                placeholder='Search stores by name, address, city, phone, or website...'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className='block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm'
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                showFilters ? 'ring-2 ring-blue-500' : ''
              }`}
            >
              <FunnelIcon className='h-4 w-4 mr-2' />
              Filters
            </button>
            {!isAIFiltering && (
              <>
                <button
                  onClick={handleAIFilter}
                  disabled={aiFilterMutation.isLoading}
                  className='inline-flex items-center justify-center h-10 px-3 py-2 border border-purple-300 shadow-sm text-sm leading-4 font-medium rounded-md text-purple-700 dark:text-purple-300 bg-purple-50 dark:bg-purple-900/20 dark:border-purple-600 hover:bg-purple-100 dark:hover:bg-purple-900/30 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50'
                >
                  {aiFilterMutation.isLoading ? (
                    <>
                      <LoadingSpinner />
                      <span className='ml-2'>Filtering...</span>
                    </>
                  ) : (
                    <>
                      <span className='mr-2'>🤖</span>
                      AI Filter
                    </>
                  )}
                </button>
                <button
                  onClick={() => zohoDedupCheckMutation.mutate()}
                  disabled={zohoDedupCheckMutation.isLoading}
                  className='inline-flex items-center justify-center h-10 px-3 py-2 border border-orange-300 shadow-sm text-sm leading-4 font-medium rounded-md text-orange-700 dark:text-orange-300 bg-orange-50 dark:bg-orange-900/20 dark:border-orange-600 hover:bg-orange-100 dark:hover:bg-orange-900/30 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50'
                >
                  {zohoDedupCheckMutation.isLoading ? (
                    <>
                      <LoadingSpinner />
                      <span className='ml-2'>Checking...</span>
                    </>
                  ) : (
                    <>
                      <span className='mr-2'>🔍</span>
                      Zoho De Dup
                    </>
                  )}
                </button>
                <button
                  onClick={() => phoneEnrichMutation.mutate()}
                  disabled={phoneEnrichMutation.isLoading}
                  className='inline-flex items-center justify-center h-10 px-3 py-2 border border-teal-300 shadow-sm text-sm leading-4 font-medium rounded-md text-teal-700 dark:text-teal-300 bg-teal-50 dark:bg-teal-900/20 dark:border-teal-600 hover:bg-teal-100 dark:hover:bg-teal-900/30 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:opacity-50'
                >
                  {phoneEnrichMutation.isLoading ? (
                    <>
                      <LoadingSpinner />
                      <span className='ml-2'>Enriching...</span>
                    </>
                  ) : (
                    <>
                      <span className='mr-2'>📞</span>
                      Enrich Phones
                    </>
                  )}
                </button>
              </>
            )}
            {selectedCount > 0 && (
              <>
                <button
                  onClick={handleBulkExportToZoho}
                  disabled={exportToZohoMutation.isLoading}
                  className='inline-flex items-center px-3 py-2 border border-green-300 shadow-sm text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed'
                >
                  {exportToZohoMutation.isLoading ? (
                    <>
                      <LoadingSpinner />
                      <span className='ml-2'>Exporting...</span>
                    </>
                  ) : (
                    <>
                      <span className='mr-2'>📤</span>
                      Export to Zoho ({selectedCount})
                    </>
                  )}
                </button>
                <button
                  onClick={handleBulkDelete}
                  disabled={bulkDeleteMutation.isLoading}
                  className='inline-flex items-center px-3 py-2 border border-red-300 shadow-sm text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed'
                >
                  {bulkDeleteMutation.isLoading ? (
                    <>
                      <LoadingSpinner />
                      <span className='ml-2'>Deleting...</span>
                    </>
                  ) : (
                    <>
                      <span className='mr-2'>🗑️</span>
                      Delete Selected ({selectedCount})
                    </>
                  )}
                </button>
              </>
            )}
            {isAIFiltering && (
              <button
                onClick={handleExitAIFilter}
                className='inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500'
              >
                <XMarkIcon className='h-4 w-4 mr-2' />
                Exit AI Filter
              </button>
            )}
            {(searchTerm ||
              stateFilter ||
              sourceFilter ||
              keywordFilter ||
              distributorFilter) &&
              !isAIFiltering && (
                <button
                  onClick={clearAllFilters}
                  className='inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500'
                >
                  <XMarkIcon className='h-4 w-4 mr-2' />
                  Clear All
                </button>
              )}
          </div>

          {/* Advanced Filters */}
          {showFilters && (
            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t border-gray-200 dark:border-gray-600'>
              {/* State Filter */}
              <div>
                <label
                  htmlFor='state-filter'
                  className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'
                >
                  State
                </label>
                <select
                  id='state-filter'
                  value={stateFilter}
                  onChange={(e) => setStateFilter(e.target.value)}
                  className='block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white'
                >
                  <option value=''>All States</option>
                  {uniqueStates.map((state) => (
                    <option key={state} value={state}>
                      {state}
                    </option>
                  ))}
                </select>
              </div>

              {/* Source Filter */}
              <div>
                <label
                  htmlFor='source-filter'
                  className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'
                >
                  Source
                </label>
                <select
                  id='source-filter'
                  value={sourceFilter}
                  onChange={(e) => setSourceFilter(e.target.value)}
                  className='block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white'
                >
                  <option value=''>All Sources</option>
                  <option value='scraped'>Scraped</option>
                  <option value='imported'>Imported</option>
                </select>
              </div>

              {/* Keyword Filter */}
              <div>
                <label
                  htmlFor='keyword-filter'
                  className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'
                >
                  Keyword
                </label>
                <select
                  id='keyword-filter'
                  value={keywordFilter}
                  onChange={(e) => setKeywordFilter(e.target.value)}
                  className='block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white'
                >
                  <option value=''>All Keywords</option>
                  {uniqueKeywords.map((keyword) => (
                    <option key={keyword} value={keyword}>
                      {keyword}
                    </option>
                  ))}
                </select>
              </div>

              {/* Distributor Filter */}
              <div>
                <label
                  htmlFor='distributor-filter'
                  className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'
                >
                  Lead Type
                </label>
                <select
                  id='distributor-filter'
                  value={distributorFilter}
                  onChange={(e) => setDistributorFilter(e.target.value)}
                  className='block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white'
                >
                  <option value=''>All Leads</option>
                  <option value='distributors'>
                    💎 Distributors/Wholesale
                  </option>
                  <option value='non-distributors'>Regular Stores</option>
                </select>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* AG Grid */}
      <div className='card'>
        <div className='card-body p-0'>
          <div
            className={
              theme === 'dark' ? 'ag-theme-quartz-dark' : 'ag-theme-quartz'
            }
            style={{
              height: 'calc(100vh - 280px)',
              width: '100%',
              overflowX: 'auto',
            }}
          >
            <AgGridReact
              rowData={filteredStores}
              columnDefs={columnDefs}
              domLayout='normal'
              suppressHorizontalScroll={false}
              defaultColDef={defaultColDef}
              components={frameworkComponents}
              pagination={true}
              paginationPageSize={50}
              paginationPageSizeSelector={[25, 50, 100, 200]}
              animateRows={true}
              rowSelection='multiple'
              enableCellTextSelection={true}
              onGridReady={onGridReady}
              onCellValueChanged={onCellValueChanged}
              onSelectionChanged={onSelectionChanged}
            />
          </div>
        </div>
      </div>

      {/* Zoho Check Confirmation Dialog */}
      {showZohoCheckDialog && storeToCheck && (
        <div className='fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50'>
          <div className='relative top-1/4 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white dark:bg-gray-800'>
            <div className='mt-3'>
              <div className='flex items-center justify-center mb-4'>
                <div className='flex items-center justify-center h-12 w-12 rounded-full bg-green-100 dark:bg-green-900'>
                  <span className='text-2xl'>✓</span>
                </div>
              </div>
              <h3 className='text-lg font-medium text-center text-gray-900 dark:text-white mb-4'>
                Check if Store Exists in Zoho
              </h3>
              <p className='text-sm text-gray-500 dark:text-gray-400 text-center mb-2'>
                <strong className='text-gray-900 dark:text-white'>
                  {storeToCheck.name}
                </strong>
              </p>
              <p className='text-sm text-gray-500 dark:text-gray-400 text-center mb-6'>
                This will check if this store exists in Zoho CRM by phone
                number.
              </p>
              <div className='flex items-center justify-end space-x-3'>
                <button
                  onClick={() => setShowZohoCheckDialog(false)}
                  className='px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 dark:bg-gray-600 dark:text-gray-300 dark:border-gray-500 dark:hover:bg-gray-500'
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmZohoCheck}
                  disabled={checkZohoMutation.isLoading}
                  className='px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50'
                >
                  {checkZohoMutation.isLoading ? 'Checking...' : 'OK'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Zoho Check Results Dialog */}
      {showZohoResultDialog && zohoCheckResult && (
        <div className='fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50'>
          <div className='relative top-1/4 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white dark:bg-gray-800'>
            <div className='mt-3'>
              <div className='flex items-center justify-center mb-4'>
                <div
                  className={`flex items-center justify-center h-12 w-12 rounded-full ${
                    zohoCheckResult.found
                      ? 'bg-green-100 dark:bg-green-900'
                      : 'bg-red-100 dark:bg-red-900'
                  }`}
                >
                  <span className='text-2xl'>
                    {zohoCheckResult.found ? '✅' : '❌'}
                  </span>
                </div>
              </div>
              <h3 className='text-lg font-medium text-center text-gray-900 dark:text-white mb-4'>
                {zohoCheckResult.found ? 'Found in Zoho!' : 'Not Found in Zoho'}
              </h3>

              {zohoCheckResult.found && zohoCheckResult.record && (
                <div className='bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-4'>
                  <p className='text-sm text-gray-600 dark:text-gray-300 mb-2'>
                    <strong className='text-gray-900 dark:text-white'>
                      Type:
                    </strong>{' '}
                    <span className='px-2 py-1 text-xs rounded bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300'>
                      {zohoCheckResult.found_as === 'account'
                        ? 'Account'
                        : 'Lead'}
                    </span>
                  </p>
                  <p className='text-sm text-gray-600 dark:text-gray-300 mb-2'>
                    <strong className='text-gray-900 dark:text-white'>
                      Name:
                    </strong>{' '}
                    {zohoCheckResult.record.name}
                  </p>
                  <p className='text-sm text-gray-600 dark:text-gray-300 mb-2'>
                    <strong className='text-gray-900 dark:text-white'>
                      Phone:
                    </strong>{' '}
                    {zohoCheckResult.record.phone}
                  </p>
                  <p className='text-sm text-gray-600 dark:text-gray-300'>
                    <strong className='text-gray-900 dark:text-white'>
                      Zoho ID:
                    </strong>{' '}
                    {zohoCheckResult.record.id}
                  </p>
                </div>
              )}

              {!zohoCheckResult.found && (
                <p className='text-sm text-gray-500 dark:text-gray-400 text-center mb-6'>
                  {zohoCheckResult.message ||
                    'This store was not found in Zoho CRM.'}
                </p>
              )}

              <div className='flex items-center justify-center space-x-3'>
                {!zohoCheckResult.found && storeToCheck && (
                  <button
                    onClick={() => {
                      exportToZohoMutation.mutate([storeToCheck.id])
                    }}
                    disabled={exportToZohoMutation.isLoading}
                    className='px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed'
                  >
                    {exportToZohoMutation.isLoading
                      ? 'Exporting...'
                      : 'Export to Zoho'}
                  </button>
                )}
                <button
                  onClick={() => {
                    setShowZohoResultDialog(false)
                    setZohoCheckResult(null)
                    setStoreToCheck(null)
                  }}
                  className='px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Store Modal */}
      {showAddStoreModal && (
        <div className='fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50'>
          <div className='relative top-20 mx-auto p-5 border w-11/12 md:w-2/3 lg:w-1/2 shadow-lg rounded-md bg-white dark:bg-gray-800'>
            <div className='mt-3'>
              {/* Modal Header */}
              <div className='flex items-center justify-between mb-4'>
                <h3 className='text-lg font-medium text-gray-900 dark:text-white'>
                  Add New Store
                </h3>
                <button
                  onClick={handleCloseAddStoreModal}
                  className='text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
                >
                  <XMarkIcon className='h-6 w-6' />
                </button>
              </div>

              {/* Modal Body */}
              <div className='space-y-4'>
                {/* Job Selection */}
                <div>
                  <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
                    Job *
                  </label>
                  <select
                    value={selectedJobForAdd}
                    onChange={(e) => setSelectedJobForAdd(e.target.value)}
                    className='block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white'
                  >
                    <option value=''>Select a job</option>
                    {(jobs?.data || []).map((job: any) => (
                      <option key={job.id} value={job.id}>
                        {job.is_import_job ? job.import_filename : job.state} -{' '}
                        {job.total_stores} stores -{' '}
                        {formatDate(job.created_at)}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Store Name */}
                <div>
                  <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
                    Store Name *
                  </label>
                  <input
                    type='text'
                    value={newStore.name}
                    onChange={(e) =>
                      setNewStore({ ...newStore, name: e.target.value })
                    }
                    className='block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white'
                    placeholder='Enter store name'
                  />
                </div>

                {/* Phone */}
                <div>
                  <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
                    Phone Number *
                  </label>
                  <input
                    type='tel'
                    value={newStore.phone}
                    onChange={(e) =>
                      setNewStore({ ...newStore, phone: e.target.value })
                    }
                    className='block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white'
                    placeholder='Enter phone number'
                  />
                </div>

                {/* Address */}
                <div>
                  <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
                    Address
                  </label>
                  <input
                    type='text'
                    value={newStore.address}
                    onChange={(e) =>
                      setNewStore({ ...newStore, address: e.target.value })
                    }
                    className='block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white'
                    placeholder='Enter street address'
                  />
                </div>

                {/* City, State, Zip */}
                <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                  <div>
                    <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
                      City
                    </label>
                    <input
                      type='text'
                      value={newStore.city}
                      onChange={(e) =>
                        setNewStore({ ...newStore, city: e.target.value })
                      }
                      className='block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white'
                      placeholder='City'
                    />
                  </div>
                  <div>
                    <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
                      State
                    </label>
                    <input
                      type='text'
                      value={newStore.state}
                      onChange={(e) =>
                        setNewStore({ ...newStore, state: e.target.value })
                      }
                      className='block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white'
                      placeholder='State'
                      maxLength={2}
                    />
                  </div>
                  <div>
                    <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
                      Zip Code
                    </label>
                    <input
                      type='text'
                      value={newStore.zip_code}
                      onChange={(e) =>
                        setNewStore({ ...newStore, zip_code: e.target.value })
                      }
                      className='block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white'
                      placeholder='Zip'
                    />
                  </div>
                </div>

                {/* Website */}
                <div>
                  <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
                    Website
                  </label>
                  <input
                    type='url'
                    value={newStore.website}
                    onChange={(e) =>
                      setNewStore({ ...newStore, website: e.target.value })
                    }
                    className='block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white'
                    placeholder='https://example.com'
                  />
                </div>

                {/* Keyword Used */}
                <div>
                  <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
                    Keyword Used
                  </label>
                  <input
                    type='text'
                    value={newStore.keyword_used}
                    onChange={(e) =>
                      setNewStore({ ...newStore, keyword_used: e.target.value })
                    }
                    className='block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white'
                    placeholder='manual'
                  />
                </div>

                {/* Rating and Rating Count */}
                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  <div>
                    <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
                      Rating
                    </label>
                    <input
                      type='number'
                      step='0.1'
                      min='0'
                      max='5'
                      value={newStore.rating}
                      onChange={(e) =>
                        setNewStore({ ...newStore, rating: e.target.value })
                      }
                      className='block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white'
                      placeholder='4.5'
                    />
                  </div>
                  <div>
                    <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
                      Rating Count
                    </label>
                    <input
                      type='number'
                      min='0'
                      value={newStore.rating_count}
                      onChange={(e) =>
                        setNewStore({
                          ...newStore,
                          rating_count: e.target.value,
                        })
                      }
                      className='block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white'
                      placeholder='100'
                    />
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className='flex items-center justify-end space-x-3 mt-6'>
                <button
                  onClick={handleCloseAddStoreModal}
                  className='px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 dark:bg-gray-600 dark:text-gray-300 dark:border-gray-500 dark:hover:bg-gray-500'
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddStore}
                  disabled={
                    addStoreMutation.isLoading ||
                    !selectedJobForAdd ||
                    !newStore.name.trim() ||
                    !newStore.phone.trim()
                  }
                  className='px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed'
                >
                  {addStoreMutation.isLoading ? 'Adding...' : 'Add Store'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Zoho De Dup Confirmation Dialog */}
      {showZohoDedupDialog && zohoDedupResult && (
        <div className='fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50'>
          <div className='relative top-1/4 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white dark:bg-gray-800'>
            <div className='mt-3'>
              <div className='flex items-center justify-center mb-4'>
                <div className='flex items-center justify-center h-12 w-12 rounded-full bg-orange-100 dark:bg-orange-900'>
                  <span className='text-2xl'>🔍</span>
                </div>
              </div>
              <h3 className='text-lg font-medium text-center text-gray-900 dark:text-white mb-4'>
                Zoho Duplicate Check Results
              </h3>
              <p className='text-sm text-gray-500 dark:text-gray-400 text-center mb-4'>
                {zohoDedupResult.duplicates_count} duplicates found
              </p>
              {zohoDedupResult.duplicates_count > 0 && (
                <div className='bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-4 max-h-60 overflow-y-auto'>
                  <p className='text-xs font-medium text-gray-700 dark:text-gray-300 mb-2'>
                    Sample duplicates (showing first 10):
                  </p>
                  <ul className='text-xs text-gray-600 dark:text-gray-400 space-y-1'>
                    {zohoDedupResult.duplicate_details
                      ?.slice(0, 10)
                      .map((dup: any, idx: number) => (
                        <li key={idx}>
                          • {dup.store_name} ({dup.found_in})
                        </li>
                      ))}
                    {zohoDedupResult.duplicates_count > 10 && (
                      <li className='text-gray-500 italic'>
                        ... and {zohoDedupResult.duplicates_count - 10} more
                      </li>
                    )}
                  </ul>
                </div>
              )}
              <div className='flex items-center justify-center space-x-3'>
                <button
                  onClick={() => {
                    setShowZohoDedupDialog(false)
                    setZohoDedupResult(null)
                  }}
                  className='px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 dark:bg-gray-600 dark:text-gray-300 dark:border-gray-500 dark:hover:bg-gray-500'
                >
                  Cancel
                </button>
                {zohoDedupResult.duplicates_count > 0 && (
                  <button
                    onClick={() => {
                      zohoDedupDeleteMutation.mutate(
                        zohoDedupResult.duplicate_store_ids
                      )
                    }}
                    disabled={zohoDedupDeleteMutation.isLoading}
                    className='px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50'
                  >
                    {zohoDedupDeleteMutation.isLoading
                      ? 'Deleting...'
                      : `Delete ${zohoDedupResult.duplicates_count} Duplicates`}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Phone Enrichment Results Dialog */}
      {showPhoneEnrichDialog && phoneEnrichResult && (
        <div className='fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50'>
          <div className='relative top-1/4 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white dark:bg-gray-800'>
            <div className='mt-3'>
              <div className='flex items-center justify-center mb-4'>
                <div className='flex items-center justify-center h-12 w-12 rounded-full bg-teal-100 dark:bg-teal-900'>
                  <span className='text-2xl'>📞</span>
                </div>
              </div>
              <h3 className='text-lg font-medium text-center text-gray-900 dark:text-white mb-4'>
                Phone Enrichment Results
              </h3>
              <div className='bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-4'>
                <div className='space-y-2 text-sm'>
                  <div className='flex justify-between text-gray-600 dark:text-gray-300'>
                    <span>Total Processed:</span>
                    <span className='font-medium text-gray-900 dark:text-white'>
                      {phoneEnrichResult.total_processed}
                    </span>
                  </div>
                  <div className='flex justify-between text-gray-600 dark:text-gray-300'>
                    <span>Phones Found:</span>
                    <span className='font-bold text-teal-600 dark:text-teal-400'>
                      {phoneEnrichResult.phones_found}
                    </span>
                  </div>
                  <hr className='border-gray-200 dark:border-gray-600' />
                  <p className='text-xs font-medium text-gray-700 dark:text-gray-300'>
                    Breakdown by stage:
                  </p>
                  <div className='flex justify-between text-gray-600 dark:text-gray-300'>
                    <span>Website Regex:</span>
                    <span className='font-medium'>
                      {phoneEnrichResult.by_stage?.website_regex || 0}
                    </span>
                  </div>
                  <div className='flex justify-between text-gray-600 dark:text-gray-300'>
                    <span>Website AI:</span>
                    <span className='font-medium'>
                      {phoneEnrichResult.by_stage?.website_ai || 0}
                    </span>
                  </div>
                  <div className='flex justify-between text-gray-600 dark:text-gray-300'>
                    <span>Search + AI:</span>
                    <span className='font-medium'>
                      {phoneEnrichResult.by_stage?.google_search_ai || 0}
                    </span>
                  </div>
                  <hr className='border-gray-200 dark:border-gray-600' />
                  <div className='flex justify-between text-gray-600 dark:text-gray-300'>
                    <span>Not Found:</span>
                    <span className='font-medium'>
                      {phoneEnrichResult.not_found}
                    </span>
                  </div>
                  <div className='flex justify-between text-gray-600 dark:text-gray-300'>
                    <span>Errors:</span>
                    <span className='font-medium'>
                      {phoneEnrichResult.errors}
                    </span>
                  </div>
                </div>
              </div>
              <div className='flex items-center justify-center'>
                <button
                  onClick={() => {
                    setShowPhoneEnrichDialog(false)
                    setPhoneEnrichResult(null)
                  }}
                  className='px-4 py-2 text-sm font-medium text-white bg-teal-600 border border-transparent rounded-md hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500'
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className='p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg'>
        <h4 className='text-sm font-medium text-blue-900 dark:text-blue-300 mb-2'>
          💡 Grid Features
        </h4>
        <ul className='text-sm text-blue-800 dark:text-blue-300 space-y-1'>
          <li>✓ Click column headers to sort</li>
          <li>✓ Use column menus (☰) for advanced filtering</li>
          <li>✓ Drag column headers to reorder</li>
          <li>✓ Resize columns by dragging edges</li>
          <li>✓ Select multiple rows with checkboxes and bulk delete</li>
          <li>✓ Double-click any cell to edit inline</li>
          <li>✓ Change page size in bottom-right corner</li>
          <li>
            <span className='inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-gradient-to-r from-yellow-400 to-orange-500 text-white shadow-sm mr-2'>
              💎 HIGH VALUE
            </span>
            = Distributor/Wholesale leads (protected from AI filtering)
          </li>
        </ul>
      </div>
    </div>
  )
}
